/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

interface AppContextValue {
  isLoginModalOpen: boolean;
  setIsLoginModalOpen: (open: boolean) => void;
  userId: string | null;
  setUserId: (id: string | null) => void;
  syncCVToServer: (cvData: unknown, templateName: string) => Promise<void>;
  loadCVFromServer: (templateName?: string) => Promise<unknown>;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(() => localStorage.getItem("cv_user_id"));

  const syncCVToServer = useCallback(async (cvData: unknown, templateName: string) => {
    if (!userId) return;
    try {
      await fetch("/api/user/cv-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, cvData, templateName }),
      });
    } catch (err) {
      console.error("Failed to sync CV:", err);
    }
  }, [userId]);

  const loadCVFromServer = useCallback(async (templateName?: string) => {
    if (!userId) return null;
    try {
      const res = await fetch(`/api/user/cv-sync?userId=${userId}${templateName ? `&templateName=${templateName}` : ""}`);
      const data = await res.json();
      return data.cvVersions?.[0]?.cv_data || null;
    } catch (err) {
      console.error("Failed to load CV:", err);
      return null;
    }
  }, [userId]);

  return (
    <AppContext.Provider value={{
      isLoginModalOpen,
      setIsLoginModalOpen,
      userId,
      setUserId,
      syncCVToServer,
      loadCVFromServer,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
}